
import { Component, Watch, Prop, Vue } from 'vue-property-decorator';

import ActionButton from '@/components/ActionButton.vue';
import ReplenishmentSimulator from '@/components/ReplenishmentSimulator.vue';
import { ReorderSettingParam } from '@/models/reorder_setting_param';
import { SupplierModel } from '@/models/supplier';
import { SupplierList } from '@/collections/suppliers';
import { EventBus } from '@/main';
import { ReplenishmentSettingHelper } from '@/helpers/ReplenishmentSettingHelper';

@Component({
  components: {
    ActionButton,
    ReplenishmentSimulator,
  },
})
export default class ReorderParamForm extends Vue {
  @Prop({ default: false })
  public isLoadingParams!: boolean;
  public settingDialog: boolean = false;
  @Prop({ default: false })
  public isPrimaryButton!: boolean;
  @Prop({ default: 'Inventory Parameters' })
  public buttonTitle!: string;
  @Prop({ default: 'mdi-cog' })
  public buttonIcon!: string;
  @Prop({ default: 'small' })
  public buttonSize!: string;
  @Prop({ default: () => new ReorderSettingParam() })
  public initReorderSettingParam!: ReorderSettingParam;
  public reorderSettingParam: ReorderSettingParam = new ReorderSettingParam();
  public suppliers: SupplierList = new SupplierList();
  @Prop({ default: false })
  public saveLoading!: boolean;
  public supplierLoading: boolean = false;
  public supplier: SupplierModel = new SupplierModel({ id: 0 });
  public supplierRule: any = [this.isSupplierValid];
  public numberRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
  ];
  public caseQuantityRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 1) || 'Case quantity must be at least 1 when specified',
  ];

  // Added back min/max rules - Now includes cross-field validation
  public minOrderRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
    (v: string) => {
      if (!v || v === '') { return true; }
      const maxVal = this.reorderSettingParam.maxOrderQuantity;
      // Only validate if max has a value
      if (maxVal === undefined || maxVal === null) { return true; }
      return Number(v) <= Number(maxVal) || 'Min must be <= Max';
    },
  ];
  public maxOrderRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
    (v: string) => {
      if (!v || v === '') { return true; }
      const minVal = this.reorderSettingParam.minOrderQuantity;
       // Only validate if min has a value
      if (minVal === undefined || minVal === null) { return true; }
      return Number(v) >= Number(minVal) || 'Max must be >= Min';
    },
  ];

  public minInventoryRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
    (v: string) => {
      if (!v || v === '') { return true; }
      const maxVal = this.reorderSettingParam.maxInventoryQuantity;
      // Only validate if max has a value
      if (maxVal === undefined || maxVal === null) { return true; }
      return Number(v) <= Number(maxVal) || 'Min must be <= Max';
    },
  ];
  public maxInventoryRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
    (v: string) => {
      if (!v || v === '') { return true; }
      const minVal = this.reorderSettingParam.minInventoryQuantity;
       // Only validate if min has a value
      if (minVal === undefined || minVal === null) { return true; }
      return Number(v) >= Number(minVal) || 'Max must be >= Min';
    },
  ];
  public minStockCoverDaysRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
    (v: string) => {
      if (!v || v === '') { return true; }
      const maxVal = this.reorderSettingParam.maxStockCoverDays;
      // Only validate if max has a value
      if (maxVal === undefined || maxVal === null) { return true; }
      return Number(v) <= Number(maxVal) || 'Min must be <= Max';
    },
  ];
  public maxStockCoverDaysRule: any = [
    (v: string) => !v || v === '' || (!isNaN(Number(v)) && Number(v) >= 0) || 'Value must be a non-negative number',
    (v: string) => {
      if (!v || v === '') { return true; }
      const minVal = this.reorderSettingParam.minStockCoverDays;
      // Only validate if min has a value
      if (minVal === undefined || minVal === null) { return true; }
      return Number(v) >= Number(minVal) || 'Max must be >= Min';
    },
  ];

  public mustSelectVariantRule: any = [
    (v: any) => !v || !v.id || 'Must select a variant',
  ];
  public isFormValid: boolean = false;
  public limitStockByDays: boolean = false;
  @Prop({ default: false })
  public isSupplierContext!: boolean;
  @Prop({ default: null })
  public supplierId!: number | null;
  @Prop({ default: 'reorder' })
  public paramType!: string;

  // Props to control field visibility
  @Prop({ default: false })
  public hideReplenishmentExclusion!: boolean;
  @Prop({ default: false })
  public hideFixOrderQuantity!: boolean;
  @Prop({ default: true })
  public showMinMaxOrderQuantity!: boolean;
  @Prop({ default: false })
  public hideFormActions!: boolean;
  @Prop({ default: false })
  public hideResetButton!: boolean;

  @Prop({ default: false })
  public allowScopeSelection!: boolean;

  @Prop({ default: 'variant' })
  public scope!: string;

  @Prop({ default: false })
  public showAllParams!: boolean;

  @Prop({ default: '' })
  public formLevelWarning!: string;

  public async created() {
    this.reorderSettingParam = new ReorderSettingParam();
    if (this.initReorderSettingParam) {
      // Create a copy of the parameter object to avoid reference issues
      this.reorderSettingParam = new ReorderSettingParam();
      Object.assign(this.reorderSettingParam, this.initReorderSettingParam);
    }
    this.supplier = new SupplierModel({ id: 0 });
    this.supplier.name = '';
    try {
      this.supplierLoading = true;
      await this.suppliers.fetch();
      this.supplierLoading = false;
    } catch (e) {
      this.supplierLoading = false;
    }
    if (this.isSupplierContext && this.supplierId) {
      this.reorderSettingParam.supplierId = this.supplierId;
    }
  }

  public isSupplierValid(v: any) {
    if (!v) {
      return 'Supplier is required';
    }
    return true;
  }

  public async saveNewSupplier() {
    if (!this.supplier.name) {
      return;
    }
    let exist = false;
    for (const item of this.suppliers.items) {
      if (item.name === this.supplier.name) {
        exist = true;
        return;
      }
    }
    if (!exist) {
      const newSupplier: SupplierModel = new SupplierModel({ id: 0 });
      newSupplier.name = this.supplier.name;
      await newSupplier.save();
      EventBus.$emit('show-snackbar', 'Supplier added successfully!');
      this.suppliers.items.push(newSupplier);
    }
  }

  public resetForm() {
    this.reorderSettingParam = new ReorderSettingParam();
    if (this.isSupplierContext && this.supplierId) {
      this.reorderSettingParam.supplierId = this.supplierId;
    }
  }

  public handleBack() {
    this.$emit('inventory-params-back');
  }

  public async applySetting() {
    const form = this.$refs.form as any;
    if (form && form.validate()) {
       // Clear any previous warning first
      this.$emit('update:form-level-warning', '');
      this.formLevelWarning = '';

      // Emit the event
      this.$emit('apply-setting', this.reorderSettingParam);
    }
  }

  // Watch initReorderSettingParam if it changed then update reorderSettingParam
  @Watch('initReorderSettingParam', { deep: true, immediate: false })
  private onInitReorderSettingParamChanged() {
    if (this.initReorderSettingParam) {
      // Create a copy of the parameter object to avoid reference issues
      this.reorderSettingParam = new ReorderSettingParam();
      Object.assign(this.reorderSettingParam, this.initReorderSettingParam);
    } else {
      // If null or undefined is passed, reset the form
      this.reorderSettingParam = new ReorderSettingParam();
    }
  }

  private async loadGlobalParams() {
    if (!this.isSupplierContext) {
      try {
        const params = await ReplenishmentSettingHelper.getGlobalParams(
          {
            product: 'variant',
            location: '',
            supplier: this.supplier ? 'specific' : 'global',
            parameter: 'global',
          },
          this.supplier?.id,
        );
        this.updateFormValues(params);
      } catch (error) {
        // Error loading global parameters
        EventBus.$emit('show-snackbar', 'Failed to load global parameters', 'error');
      }
    }
  }

  private updateFormValues(params: ReorderSettingParam) {
    this.reorderSettingParam.leadtime = params.leadtime;
    this.reorderSettingParam.orderCycle = params.orderCycle;
    this.reorderSettingParam.fixOrderQuantity = params.fixOrderQuantity;
    this.reorderSettingParam.minInventoryQuantity = params.minInventoryQuantity;
    this.reorderSettingParam.maxInventoryQuantity = params.maxInventoryQuantity;
    this.reorderSettingParam.caseQuantity = params.caseQuantity;
    this.reorderSettingParam.replenishmentExclusion = params.replenishmentExclusion;
  }

  get internalScope(): string {
    return this.scope;
  }
  set internalScope(value: string) {
    this.$emit('update:scope', value);
  }
}
